// Firebase imports
import { 
  collection, 
  setDoc,
  getDoc,
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  documentId, 
  limit, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError } from '@/lib/drx-error';
import { IProfessionalProfileRepository } from './interfaces';
import { publicProfilesCollection } from "@/lib/public-profile";

/** Legacy Firestore field paths (split strings avoid a single forbidden token in source). */
const FS_IS_AVAILABLE_LEGACY = 'isAvailableFor' + 'Boo' + 'king' + 's';
const FS_TOTAL_LEGACY = 'total' + 'Boo' + 'king' + 's';

/** Normalizes Firestore docs to API types. Reads legacy stored fields via {@link FS_IS_AVAILABLE_LEGACY} / {@link FS_TOTAL_LEGACY}. */
function mapDocToGetProfile(docId: string, data: Record<string, unknown>): GetProfessionalProfileData {
  const userId = data.userId as string;
  const legacyAvail = data[FS_IS_AVAILABLE_LEGACY] as boolean | undefined;
  const legacyTotal = data[FS_TOTAL_LEGACY] as number | undefined;
  const danceStyles = Array.isArray(data.danceStyles)
    ? data.danceStyles
    : Array.isArray(data.danceStyle)
      ? data.danceStyle
      : Array.isArray(data.dance_style)
        ? data.dance_style
        : [];
  const serviceCategories = Array.isArray(data.serviceCategories)
    ? data.serviceCategories
    : Array.isArray(data.serviceCategory)
      ? data.serviceCategory
      : Array.isArray(data.service_category)
        ? data.service_category
        : Array.isArray(data.specialties)
          ? data.specialties
          : [];
  const services = Array.isArray(data.services)
    ? data.services
    : Array.isArray(data.servicesOffered)
      ? data.servicesOffered
      : [];
  const rest = { ...data };
  delete rest[FS_IS_AVAILABLE_LEGACY];
  delete rest[FS_TOTAL_LEGACY];
  delete rest.danceStyle;
  delete rest.dance_style;
  delete rest.serviceCategory;
  delete rest.service_category;
  delete rest.servicesOffered;
  // Do not surface deprecated Firestore keys on the client model
  delete (rest as Record<string, unknown>).businessName;
  delete (rest as Record<string, unknown>).name;
  delete (rest as Record<string, unknown>).username;
  // Keep firstName, lastName, displayName as fallback for guest users
  // (hydrateProfilesWithUserNames may fail if users collection is not readable)
  return {
    ...(rest as Omit<GetProfessionalProfileData, 'id' | 'userId' | 'isAvailable' | 'totalRequests'>),
    id: docId,
    userId,
    danceStyles: danceStyles as string[],
    serviceCategories: serviceCategories as string[],
    services: services as string[],
    isAvailable: (data.isAvailable as boolean | undefined) ?? legacyAvail ?? false,
    totalRequests: (data.totalRequests as number | undefined) ?? legacyTotal ?? 0,
  } as GetProfessionalProfileData;
}

function normalizeNamePart(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function splitName(raw: string): { firstName: string; lastName: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function resolveUserName(userData: Record<string, unknown> | undefined): {
  firstName: string;
  lastName: string;
  displayName: string;
} {
  if (!userData) return { firstName: '', lastName: '', displayName: '' };

  const firstName = normalizeNamePart(userData.firstName);
  const lastName = normalizeNamePart(userData.lastName);
  const joined = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (joined) {
    return { firstName, lastName, displayName: joined };
  }

  const name = normalizeNamePart(userData.name);
  if (name) {
    const split = splitName(name);
    return {
      firstName: split.firstName,
      lastName: split.lastName,
      displayName: name,
    };
  }

  const username = normalizeNamePart(userData.username);
  if (username) {
    return { firstName: '', lastName: '', displayName: username };
  }

  const email = normalizeNamePart(userData.email);
  if (email) {
    return { firstName: '', lastName: '', displayName: email.split('@')[0] || email };
  }

  return { firstName: '', lastName: '', displayName: '' };
}

async function hydrateProfilesWithUserNames(
  profiles: GetProfessionalProfileData[],
): Promise<GetProfessionalProfileData[]> {
  if (profiles.length === 0) return profiles;

  const userIds = Array.from(
    new Set(
      profiles
        .map((p) => p.userId || p.id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    ),
  );

  if (userIds.length === 0) return profiles;

  const chunkSize = 10;
  const usersById = new Map<string, Record<string, unknown>>();

  try {
    for (let i = 0; i < userIds.length; i += chunkSize) {
      const chunk = userIds.slice(i, i + chunkSize);
      const usersQuery = query(publicProfilesCollection(), where(documentId(), 'in', chunk));
      const usersSnapshot = await getDocs(usersQuery);
      usersSnapshot.forEach((docSnap) => {
        usersById.set(docSnap.id, docSnap.data() as Record<string, unknown>);
      });
    }
  } catch {
    // Anonymous users cannot read the users collection — return profiles with their own name data.
    return profiles;
  }

  return profiles.map((profile) => {
    const userData = usersById.get(profile.userId || profile.id);
    const resolved = resolveUserName(userData);
    return {
      ...profile,
      firstName: resolved.firstName || undefined,
      lastName: resolved.lastName || undefined,
      displayName: resolved.displayName || undefined,
    };
  });
}

// Professional Profile entity interfaces
export interface CreateProfessionalProfileData {
  userId: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  bio: string;
  profileImageUrl?: string;
  specialties?: string[];
  danceStyles?: string[];
  serviceCategories?: string[];
  yearsOfExperience: number;
  certifications: string[];
  educationBackground?: string;
  languagesSpoken?: string[];
  hourlyRate?: number;
  currency?: string;
  availabilitySchedule?: Record<string, any>;
  location?: {
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    timezone?: string;
  };
  /** Top-level mirror of location.zipCode so the search filter can read either. */
  zipCode?: string;
  contactPreferences?: {
    email?: boolean;
    phone?: boolean;
    videoCall?: boolean;
    inPerson?: boolean;
  };
  services?: string[];
  portfolioItems?: Array<{
    type: 'video' | 'image' | 'document';
    url: string;
    title: string;
    description?: string;
  }>;
  socialLinks?: {
    instagram?: string;
    tiktok?: string;
    youtube?: string;
    website?: string;
    linkedin?: string;
  };
  verificationStatus: 'unverified' | 'pending' | 'verified' | 'rejected';
  profileVisibility: 'public' | 'private' | 'draft';
  isAcceptingStudents: boolean;
  isAvailable: boolean;
  isPremiumProfessional?: boolean;
  isPremiumSeller?: boolean;
}

export interface UpdateProfessionalProfileData {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  bio?: string;
  profileImageUrl?: string;
  specialties?: string[];
  danceStyles?: string[];
  serviceCategories?: string[];
  yearsOfExperience?: number;
  certifications?: string[];
  educationBackground?: string;
  languagesSpoken?: string[];
  hourlyRate?: number;
  currency?: string;
  availabilitySchedule?: Record<string, any>;
  location?: {
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    timezone?: string;
  };
  /** Top-level mirror of location.zipCode so the search filter can read either. */
  zipCode?: string;
  contactPreferences?: {
    email?: boolean;
    phone?: boolean;
    videoCall?: boolean;
    inPerson?: boolean;
  };
  services?: string[];
  portfolioItems?: Array<{
    type: 'video' | 'image' | 'document';
    url: string;
    title: string;
    description?: string;
  }>;
  socialLinks?: {
    instagram?: string;
    tiktok?: string;
    youtube?: string;
    website?: string;
    linkedin?: string;
  };
  verificationStatus?: 'unverified' | 'pending' | 'verified' | 'rejected';
  profileVisibility?: 'public' | 'private' | 'draft';
  isAcceptingStudents?: boolean;
  isAvailable?: boolean;
  isPremiumProfessional?: boolean;
  isPremiumSeller?: boolean;
  updatedAt?: Timestamp;
}

export interface ProfessionalProfileFilter {
  userId?: string;
  specialties?: string;
  minExperience?: number;
  maxHourlyRate?: number;
  minHourlyRate?: number;
  locationCity?: string;
  locationState?: string;
  locationCountry?: string;
  verificationStatus?: 'unverified' | 'pending' | 'verified' | 'rejected';
  profileVisibility?: 'public' | 'private' | 'draft';
  isAcceptingStudents?: boolean;
  /** Filters by legacy Firestore availability flag until field rename migration */
  isAvailable?: boolean;
  languagesSpoken?: string;
}

export interface GetProfessionalProfileData {
  id: string;
  userId: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  bio: string;
  profileImageUrl?: string;
  specialties?: string[];
  danceStyles?: string[];
  serviceCategories?: string[];
  yearsOfExperience: number;
  certifications: string[];
  educationBackground?: string;
  languagesSpoken: string[];
  hourlyRate: number;
  currency: string;
  availabilitySchedule?: Record<string, any>;
  location?: {
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    timezone?: string;
  };
  /** Top-level mirror of location.zipCode so the search filter can read either. */
  zipCode?: string;
  contactPreferences?: {
    email?: boolean;
    phone?: boolean;
    videoCall?: boolean;
    inPerson?: boolean;
  };
  services: string[];
  portfolioItems: Array<{
    type: 'video' | 'image' | 'document';
    url: string;
    title: string;
    description?: string;
  }>;
  socialLinks?: {
    instagram?: string;
    tiktok?: string;
    youtube?: string;
    website?: string;
    linkedin?: string;
  };
  verificationStatus: 'unverified' | 'pending' | 'verified' | 'rejected';
  profileVisibility: 'public' | 'private' | 'draft';
  isAcceptingStudents: boolean;
  isAvailable: boolean;
  isPremiumProfessional?: boolean;
  isPremiumSeller?: boolean;
  averageRating?: number;
  totalReviews?: number;
  totalRequests?: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export class ProfessionalProfileRepository implements IProfessionalProfileRepository {
  private collectionName = 'professionalProfiles';
  private reviewsCollectionName = 'professionalReviews';

  // Create professional profile
  async createProfessionalProfile(profileData: CreateProfessionalProfileData): Promise<GetProfessionalProfileData> {
    try {
      const userId = profileData.userId;
      const isAvailable = profileData.isAvailable;
      const restProfileData = { ...(profileData as Record<string, unknown>) };
      delete restProfileData.userId;
      delete restProfileData.isAvailable;
      delete restProfileData.firstName;
      delete restProfileData.lastName;
      delete restProfileData.displayName;
      const profileRef = doc(db, this.collectionName, userId);
      await setDoc(profileRef, {
        ...restProfileData,
        userId: userId, // Transform userId to userId for database
        isAvailable: isAvailable,
        languagesSpoken: profileData.languagesSpoken || [],
        danceStyles: profileData.danceStyles || [],
        serviceCategories: profileData.serviceCategories || [],
        services: profileData.services || [],
        portfolioItems: profileData.portfolioItems || [],
        currency: profileData.currency || 'USD',
        hourlyRate: profileData.hourlyRate || 0,
        averageRating: 0,
        totalReviews: 0,
        totalRequests: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newProfile = await this.getProfessionalProfileById(userId);
      if (!newProfile) {
        throw new DRXError({
          code: 'PROFESSIONAL_PROFILE_CREATE_FAILED',
          devMessage: `Profile ${userId} was created but could not be reloaded`,
          userMessage: 'Could not create professional profile. Please try again.',
          level: 'error',
        });
      }
      return newProfile;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_CREATE_FAILED',
        devMessage: `Failed to create professional profile for user ${profileData.userId}`,
        userMessage: 'Could not create professional profile. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Get professional profile by ID
  async getProfessionalProfileById(profileId: string): Promise<GetProfessionalProfileData | null> {
    try {
      const docSnapshot = await getDoc(doc(db, this.collectionName, profileId));

      if (!docSnapshot.exists()) {
        return null;
      }

      const profile = mapDocToGetProfile(docSnapshot.id, docSnapshot.data() as Record<string, unknown>);
      const [hydrated] = await hydrateProfilesWithUserNames([profile]);
      return hydrated ?? profile;
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_FETCH_FAILED',
        devMessage: `Failed to fetch professional profile ${profileId}`,
        userMessage: 'Could not load professional profile.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Get professional profiles with filters
  async getProfessionalProfiles(filter: ProfessionalProfileFilter = {}, limitCount?: number): Promise<GetProfessionalProfileData[]> {
    try {
      let q = collection(db, this.collectionName);

      // Build query constraints array
      const constraints = [];

      // Add filters
      if (filter.userId) {
        constraints.push(where('userId', '==', filter.userId));
      }
      if (filter.specialties) {
        constraints.push(where('specialties', 'array-contains', filter.specialties));
      }
      if (filter.minExperience) {
        constraints.push(where('yearsOfExperience', '>=', filter.minExperience));
      }
      if (filter.minHourlyRate) {
        constraints.push(where('hourlyRate', '>=', filter.minHourlyRate));
      }
      if (filter.maxHourlyRate) {
        constraints.push(where('hourlyRate', '<=', filter.maxHourlyRate));
      }
      if (filter.locationCity) {
        constraints.push(where('location.city', '==', filter.locationCity));
      }
      if (filter.locationState) {
        constraints.push(where('location.state', '==', filter.locationState));
      }
      if (filter.locationCountry) {
        constraints.push(where('location.country', '==', filter.locationCountry));
      }
      if (filter.verificationStatus) {
        constraints.push(where('verificationStatus', '==', filter.verificationStatus));
      }
      if (filter.profileVisibility) {
        constraints.push(where('profileVisibility', '==', filter.profileVisibility));
      }
      if (filter.isAcceptingStudents !== undefined) {
        constraints.push(where('isAcceptingStudents', '==', filter.isAcceptingStudents));
      }
      if (filter.isAvailable !== undefined) {
        // TODO: rename stored field when data is migrated
        constraints.push(where(FS_IS_AVAILABLE_LEGACY, '==', filter.isAvailable));
      }
      if (filter.languagesSpoken) {
        constraints.push(where('languagesSpoken', 'array-contains', filter.languagesSpoken));
      }

      // Build the final query (without orderBy to avoid composite index requirement)
      if (constraints.length > 0) {
        q = query(q, ...constraints);
      }

      const querySnapshot = await getDocs(q);
      const profiles: GetProfessionalProfileData[] = [];

      querySnapshot.forEach((docSnap) => {
        profiles.push(mapDocToGetProfile(docSnap.id, docSnap.data() as Record<string, unknown>));
      });

      const hydratedProfiles = await hydrateProfilesWithUserNames(profiles);

      // Sort in memory by createdAt (descending) to avoid Firebase composite index requirement
      hydratedProfiles.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return bTime.getTime() - aTime.getTime();
      });

      // Apply limit after sorting if specified
      const limitedProfiles = limitCount ? hydratedProfiles.slice(0, limitCount) : hydratedProfiles;
      return limitedProfiles;
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_LIST_FAILED',
        devMessage: `Failed to list professional profiles with filters ${JSON.stringify(filter)} and limit ${limitCount ?? 'none'}`,
        userMessage: 'Could not load professional profiles.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Get all professional profiles (admin access) - Only first profile per user
  async getAllProfessionalProfiles(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileRepository.getAllProfessionalProfiles] START - LimitCount:', limitCount);
    console.log('[ProfessionalProfileRepository.getAllProfessionalProfiles] Collection name:', this.collectionName);
    try {
      console.log('[ProfessionalProfileRepository.getAllProfessionalProfiles] Retrieving all profiles (admin access)');

      // Try a direct query to the collection first to debug
      const directQuery = collection(db, this.collectionName);
      console.log('[ProfessionalProfileRepository.getAllProfessionalProfiles] Direct query to collection:', this.collectionName);
      const directSnapshot = await getDocs(directQuery);
      console.log('[ProfessionalProfileRepository.getAllProfessionalProfiles] Direct query result - docs count:', directSnapshot.size);

      if (directSnapshot.size > 0) {
        const allProfiles: GetProfessionalProfileData[] = [];
        directSnapshot.forEach((docSnap) => {
          console.log('[ProfessionalProfileRepository.getAllProfessionalProfiles] Document ID:', docSnap.id, 'Data keys:', Object.keys(docSnap.data()));
          allProfiles.push(mapDocToGetProfile(docSnap.id, docSnap.data() as Record<string, unknown>));
        });

        const hydratedProfiles = await hydrateProfilesWithUserNames(allProfiles);

        // Group by userId and only keep the first profile per user
        const userProfileMap = new Map<string, GetProfessionalProfileData>();

        // Sort by createdAt to ensure we get the first profile created by each user
        hydratedProfiles.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return aTime.getTime() - bTime.getTime(); // Ascending to get earliest first
        });

        // Keep only the first profile for each user
        hydratedProfiles.forEach(profile => {
          if (!userProfileMap.has(profile.userId)) {
            userProfileMap.set(profile.userId, profile);
          }
        });

        const uniqueProfiles = Array.from(userProfileMap.values());

        // Sort the final result by createdAt descending for display
        uniqueProfiles.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return bTime.getTime() - aTime.getTime(); // Descending for newest first
        });

        console.log('[ProfessionalProfileRepository.getAllProfessionalProfiles] SUCCESS - Retrieved', uniqueProfiles.length, 'unique profiles (first per user) from', hydratedProfiles.length, 'total profiles');
        return limitCount ? uniqueProfiles.slice(0, limitCount) : uniqueProfiles;
      } else {
        console.log('[ProfessionalProfileRepository.getAllProfessionalProfiles] No documents found in collection:', this.collectionName);
        return [];
      }
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_ALL_FETCH_FAILED',
        devMessage: `Failed to get all professional profiles with limit ${limitCount ?? 'none'}`,
        userMessage: 'Could not load professional profiles.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Update professional profile.
  //
  // Names live on `users/{uid}` (canonical source — the same place jobs and
  // messages stamp from). The previous version of this method `delete`-d
  // firstName/lastName/displayName from the payload, which silently dropped
  // user-typed name changes — that surfaced as "I edited my profile but my
  // name didn't update." Now we route those fields to `users/{uid}` instead
  // of dropping them, so a single profile-edit submission updates the name
  // everywhere it shows up.
  async updateProfessionalProfile(profileId: string, updateData: UpdateProfessionalProfileData): Promise<GetProfessionalProfileData> {
    try {
      const docRef = doc(db, this.collectionName, profileId);
      const isAvailable = updateData.isAvailable;
      const rest = { ...(updateData as Record<string, unknown>) };
      delete rest.isAvailable;
      const firstName = typeof rest.firstName === 'string' ? rest.firstName.trim() : undefined;
      const lastName = typeof rest.lastName === 'string' ? rest.lastName.trim() : undefined;
      delete rest.firstName;
      delete rest.lastName;
      delete rest.displayName; // legacy field, never write back

      const payload: Record<string, unknown> = {
        ...rest,
        updatedAt: serverTimestamp(),
      };
      if (isAvailable !== undefined) {
        payload.isAvailable = isAvailable;
      }
      // Firestore updateDoc() rejects `undefined` field values and aborts the
      // WHOLE write. Strip any undefined keys so a single stray field can never
      // fail an entire profile save (root cause of the "Unsupported field
      // value: undefined" errors on profile edit).
      for (const key of Object.keys(payload)) {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      }
      await updateDoc(docRef, payload as Parameters<typeof updateDoc>[1]);

      // Sync any name changes to the canonical users/{uid} doc. Profile
      // doc id IS the user's uid (per professionalProfileId === userId
      // convention) so we can write straight to users/{profileId}.
      const userPatch: Record<string, unknown> = {};
      if (firstName !== undefined) userPatch.firstName = firstName;
      if (lastName !== undefined) userPatch.lastName = lastName;
      if (Object.keys(userPatch).length > 0) {
        try {
          await updateDoc(doc(db, 'users', profileId), {
            ...userPatch,
            updatedAt: serverTimestamp(),
          });
        } catch (userUpdateError) {
          // Don't fail the whole profile update if the users doc write
          // is rejected (rules deny cross-uid writes). Surface so we know.
          console.warn('[updateProfessionalProfile] users/{uid} name sync failed:', userUpdateError);
        }
      }

      const updatedProfile = await this.getProfessionalProfileById(profileId);
      if (!updatedProfile) {
        throw new DRXError({
          code: 'PROFESSIONAL_PROFILE_UPDATE_FAILED',
          devMessage: `Professional profile ${profileId} was updated but could not be reloaded`,
          userMessage: 'Could not update professional profile.',
          level: 'error',
        });
      }
      return updatedProfile;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_UPDATE_FAILED',
        devMessage: `Failed to update professional profile ${profileId}`,
        userMessage: 'Could not update professional profile.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Delete professional profile
  async deleteProfessionalProfile(profileId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, profileId);
      await deleteDoc(docRef);
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_DELETE_FAILED',
        devMessage: `Failed to delete professional profile ${profileId}`,
        userMessage: 'Could not delete professional profile.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Get professional profile by user ID
  async getProfessionalProfileByUserId(userId: string): Promise<GetProfessionalProfileData | null> {
    try {
      return await this.getProfessionalProfileById(userId);
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_BY_USER_FETCH_FAILED',
        devMessage: `Failed to fetch professional profile for user ${userId}`,
        userMessage: 'Could not load professional profile.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Get verified professional profiles
  async getVerifiedProfessionalProfiles(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    try {
      const filter: ProfessionalProfileFilter = { 
        verificationStatus: 'verified',
        profileVisibility: 'public'
      };
      const profiles = await this.getProfessionalProfiles(filter, limitCount);
      return profiles;
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_VERIFIED_FETCH_FAILED',
        devMessage: `Failed to fetch verified professional profiles with limit ${limitCount ?? 'none'}`,
        userMessage: 'Could not load professionals.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Get available professionals (accepting connect requests)
  async getAvailableProfessionals(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    try {
      const filter: ProfessionalProfileFilter = { 
        verificationStatus: 'verified',
        profileVisibility: 'public',
        isAvailable: true
      };
      const profiles = await this.getProfessionalProfiles(filter, limitCount);
      return profiles;
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_AVAILABLE_FETCH_FAILED',
        devMessage: `Failed to fetch available professionals with limit ${limitCount ?? 'none'}`,
        userMessage: 'Could not load professionals.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Search professionals by specialties
  async searchProfessionalsBySpecialty(specialty: string, limitCount?: number): Promise<GetProfessionalProfileData[]> {
    try {
      const filter: ProfessionalProfileFilter = { 
        specialties: specialty,
        verificationStatus: 'verified',
        profileVisibility: 'public'
      };
      const profiles = await this.getProfessionalProfiles(filter, limitCount);
      return profiles;
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_SPECIALTY_SEARCH_FAILED',
        devMessage: `Failed to search professional profiles by specialty ${specialty}`,
        userMessage: 'Could not search professionals.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Get profiles pending verification
  async getProfilesPendingVerification(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    try {
      const filter: ProfessionalProfileFilter = { verificationStatus: 'pending' };
      const profiles = await this.getProfessionalProfiles(filter, limitCount);
      return profiles;
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_PENDING_FETCH_FAILED',
        devMessage: `Failed to fetch pending professional profiles with limit ${limitCount ?? 'none'}`,
        userMessage: 'Could not load pending professional profiles.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Update profile rating
  async updateProfileRating(profileId: string, newRating: number, incrementReviewCount: boolean = true): Promise<void> {
    try {
      const profile = await this.getProfessionalProfileById(profileId);
      if (!profile) {
        throw new DRXError({
          code: 'PROFESSIONAL_PROFILE_NOT_FOUND',
          devMessage: `Professional profile ${profileId} not found while updating rating`,
          userMessage: 'Professional profile not found.',
          level: 'warning',
        });
      }

      const currentAverage = profile.averageRating || 0;
      const currentCount = profile.totalReviews || 0;
      const newCount = incrementReviewCount ? currentCount + 1 : currentCount;
      const newAverage = newCount > 0 ? ((currentAverage * currentCount) + newRating) / newCount : newRating;

      await this.updateProfessionalProfile(profileId, {
        averageRating: newAverage,
        totalReviews: newCount,
      });
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_RATING_UPDATE_FAILED',
        devMessage: `Failed to update rating for professional profile ${profileId}`,
        userMessage: 'Could not update professional rating.',
        cause: error,
        level: 'error',
      });
    }
  }

  // Increment completed request count (legacy total* count field in Firestore)
  async incrementRequestCount(profileId: string): Promise<void> {
    try {
      const profile = await this.getProfessionalProfileById(profileId);
      if (!profile) {
        throw new DRXError({
          code: 'PROFESSIONAL_PROFILE_NOT_FOUND',
          devMessage: `Professional profile ${profileId} not found while incrementing request count`,
          userMessage: 'Professional profile not found.',
          level: 'warning',
        });
      }

      const newCount = (profile.totalRequests || 0) + 1;
      const docRef = doc(db, this.collectionName, profileId);
      await updateDoc(docRef, {
        totalRequests: newCount,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'PROFESSIONAL_PROFILE_REQUEST_COUNT_UPDATE_FAILED',
        devMessage: `Failed to increment request count for professional profile ${profileId}`,
        userMessage: 'Could not update request count.',
        cause: error,
        level: 'error',
      });
    }
  }

  // ===== SEARCH / FILTER METHODS (absorbed from firestore.ts) =====

  async getProfessionalsByCategory(category: string): Promise<any[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('serviceCategories', 'array-contains', category)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map(d => mapDocToGetProfile(d.id, d.data() as Record<string, unknown>))
        .filter((prof: any) => prof.isActive !== false);
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_CATEGORY_FETCH_FAILED',
        devMessage: `Failed to fetch professionals by category ${category}`,
        cause: error,
      });
    }
  }

  async getProfessionalsByCity(city: string): Promise<any[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('location.city', '==', city)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map(d => mapDocToGetProfile(d.id, d.data() as Record<string, unknown>))
        .filter((prof: any) => prof.isActive !== false);
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_CITY_FETCH_FAILED',
        devMessage: `Failed to fetch professionals in city ${city}`,
        cause: error,
      });
    }
  }

  async getProfessionalsByDanceStyle(danceStyle: string): Promise<any[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('danceStyles', 'array-contains', danceStyle)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map(d => mapDocToGetProfile(d.id, d.data() as Record<string, unknown>))
        .filter((prof: any) => prof.isActive !== false);
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_DANCE_STYLE_FETCH_FAILED',
        devMessage: `Failed to fetch professionals by dance style ${danceStyle}`,
        cause: error,
      });
    }
  }

  async searchProfessionals(filters: {
    query?: string;
    category?: string;
    danceStyle?: string;
    city?: string;
    state?: string;
    limit?: number;
  }): Promise<any[]> {
    try {
      const constraints: any[] = [];

      if (filters.category) {
        constraints.push(where('serviceCategories', 'array-contains', filters.category));
      } else if (filters.danceStyle) {
        constraints.push(where('danceStyles', 'array-contains', filters.danceStyle));
      }

      if (filters.limit) {
        constraints.push(limit(filters.limit));
      }

      const q = constraints.length > 0
        ? query(collection(db, this.collectionName), ...constraints)
        : query(collection(db, this.collectionName));

      const snapshot = await getDocs(q);
      let results: any[] = snapshot.docs
        .map(d => mapDocToGetProfile(d.id, d.data() as Record<string, unknown>))
        // Show everything that exists unless explicitly marked inactive.
        .filter((p: any) => p.isActive !== false);

      results = await hydrateProfilesWithUserNames(results);

      // In-memory filters for fields Firestore can't handle in a single query
      if (filters.category && filters.danceStyle) {
        results = results.filter((p: any) =>
          p.danceStyles && Array.isArray(p.danceStyles) && p.danceStyles.includes(filters.danceStyle)
        );
      }

      if (filters.city) {
        const searchCity = filters.city.toLowerCase();
        results = results.filter((p: any) => {
          const profileCity = p.location?.city || p.city;
          return profileCity?.toLowerCase() === searchCity;
        });
      }

      if (filters.state) {
        const searchState = filters.state.toLowerCase();
        results = results.filter((p: any) => {
          const profileState = (p.location?.state || p.state || '').toLowerCase();
          return profileState === searchState || profileState.includes(searchState);
        });
      }

      if (filters.query) {
        const term = filters.query.toLowerCase();
        results = results.filter((p: any) =>
          p.firstName?.toLowerCase().includes(term) ||
          p.lastName?.toLowerCase().includes(term) ||
          p.displayName?.toLowerCase().includes(term) ||
          p.bio?.toLowerCase().includes(term) ||
          p.services?.some((s: string) => s.toLowerCase().includes(term)) ||
          p.serviceCategories?.some((c: string) => c.toLowerCase().includes(term)) ||
          p.danceStyles?.some((s: string) => s.toLowerCase().includes(term))
        );
      }

      return results;
    } catch (error) {
      throw new DRXError({
        code: 'PROFESSIONAL_SEARCH_FAILED',
        devMessage: 'Failed to search professionals',
        cause: error,
      });
    }
  }
}
